import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LandTourCategory, LandTourCompulsoryCharge } from "../../../../interfaces/land-tour.interface";
import { currencyDisplay } from "../../../../utils/helpers";
import { DateSelection, PaxCounts, pricingPaxCount } from "./ChooseYourTour";
import { usePriceQuote } from "../usePriceQuote";
import { customizeTourHref } from "../customizeTourHref";
import CompulsoryChargesModal from "./CompulsoryChargesModal";

const ICONS = "/iceholidays-assets/images/icons";

interface Props {
  landTourId: number | undefined;
  tourCode?: string;
  destination?: string;
  selectedCategory: LandTourCategory | undefined;
  selectedDate: DateSelection | undefined;
  paxCounts: PaxCounts;
  compulsoryCharges?: LandTourCompulsoryCharge[];
}

export default function GroundTourSidebar({
  landTourId,
  tourCode,
  destination,
  selectedCategory,
  selectedDate,
  paxCounts,
  compulsoryCharges = [],
}: Props) {
  const [showCompulsory, setShowCompulsory] = useState(false);

  // "Starting From" + the save-amount strikethrough (issue #629 (c)1/2) - the per-adult price
  // for the current category/date/headcount, from the server once calculate_price ships and
  // computed locally until then. See usePriceQuote for the fallback formula.
  const { quote } = usePriceQuote({
    landTourId,
    category: selectedCategory,
    date: selectedDate?.date,
    priceDate: selectedDate?.priceDate,
    quantity: pricingPaxCount(paxCounts),
  });

  const contactHref = customizeTourHref({
    tourCode,
    destination,
    departDate: selectedDate?.date,
  });

  return (
    <aside className="ground-tour-show-v2__sidebar">
      {/* No Total/breakdown/Next: this page has no booking flow yet, only the Contact Us link. */}
      <div className="ground-tour-show-v2__sidebar-footer">
        {quote && (
          <div className="ground-tour-show-v2__sidebar-price-block">
            <div className="ground-tour-show-v2__sidebar-price-group">
              <span className="ground-tour-show-v2__sidebar-total-label">Starting from</span>

              {quote.saveAmount > 0 && (
                <div className="ground-tour-show-v2__sidebar-save-row">
                  <span className="ground-tour-show-v2__sidebar-strikethrough">
                    {currencyDisplay("MYR", quote.beforeSaveAmount)}
                  </span>
                  <span className="ground-tour-show-v2__sidebar-save-badge">
                    Save {currencyDisplay("MYR", quote.saveAmount)}
                  </span>
                </div>
              )}

              <div className="ground-tour-show-v2__sidebar-price">
                <span className="ground-tour-show-v2__sidebar-amount">{currencyDisplay("MYR", quote.price)}</span>
                <span className="ground-tour-show-v2__sidebar-suffix">/ person</span>
              </div>
            </div>

            <span className="ground-tour-show-v2__sidebar-taxes-note">(incl. taxes & fees)</span>
          </div>
        )}

        {compulsoryCharges.length > 0 && (
          <button
            type="button"
            className="ground-tour-show-v2__sidebar-breakdown"
            onClick={() => setShowCompulsory(true)}
          >
            View Compulsory Charges
          </button>
        )}

        <Link to={contactHref} className="ground-tour-show-v2__sidebar-contact">
          <img src={`${ICONS}/icon-contact-us.svg`} alt="" aria-hidden="true" />
          Contact Us
        </Link>
      </div>

      {compulsoryCharges.length > 0 && (
        <CompulsoryChargesModal
          open={showCompulsory}
          onClose={() => setShowCompulsory(false)}
          charges={compulsoryCharges}
        />
      )}
    </aside>
  );
}
